import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { nearMissService } from '../../../services/nearMissService';
import { dailyTaskService } from '../../../services/taskService';
import { siteService } from '../../../services/siteService';
import { companyService } from '../../../services/companyService';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import MobilePhotoInput from '../../../components/ui/MobilePhotoInput';

const RISK_LEVELS = ['Low', 'Medium', 'High'];

const ReportNearMissModal = ({ isOpen, onClose, onSuccess }) => {
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const today = new Date()?.toISOString()?.split('T')?.[0];

  const [form, setForm] = useState({
    site_id: '',
    date: today,
    location: '',
    description: '',
    risk_level: 'Medium',
    follow_up_task: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      loadSites();
      setForm({ site_id: '', date: today, location: '', description: '', risk_level: 'Medium', follow_up_task: false });
      setPhotoFiles([]);
      setErrors({});
    }
  }, [isOpen]);

  const loadSites = async () => {
    setSitesLoading(true);
    try {
      const { data } = await siteService?.getAllSites();
      setSites(data || []);
      if (data?.length === 1) {
        setForm(prev => ({ ...prev, site_id: data?.[0]?.id }));
      }
    } catch {
      // ignore
    } finally {
      setSitesLoading(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form?.site_id) errs.site_id = 'Please select a site';
    if (!form?.date) errs.date = 'Date is required';
    if (!form?.description?.trim()) errs.description = 'Description is required';
    if (!form?.risk_level) errs.risk_level = 'Risk level is required';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleFileChange = (files) => {
    const validFiles = Array.from(files)?.filter(f => f?.type?.startsWith('image/'));
    setPhotoFiles(prev => [...prev, ...validFiles]?.slice(0, 10));
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    handleFileChange(e?.dataTransfer?.files);
  };

  const removePhoto = (index) => {
    setPhotoFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const { data: nearMiss, error } = await nearMissService?.createNearMiss(form);
      if (error) throw new Error(error);

      if (!nearMiss?._demo && photoFiles?.length > 0) {
        await Promise.all(
          photoFiles?.map(file => nearMissService?.uploadNearMissPhoto(file, nearMiss?.id))
        );
      }

      if (form?.follow_up_task && !nearMiss?._demo) {
        const { data: company } = await companyService?.getUserCompany();
        await dailyTaskService?.createMany([{
          title: `Follow-up: ${form?.description?.substring(0, 80)}`,
          description: `Follow-up task created from near miss report. Location: ${form?.location || 'N/A'}. Risk level: ${form?.risk_level}.`,
          site_id: form?.site_id,
          date: today,
          category: 'Safety',
          required: true,
          status: 'pending',
          company_id: company?.id,
        }]);
      }

      toast?.success('Near miss reported successfully');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error('[ReportNearMissModal] submit failed:', err);
      toast?.error(err?.message || 'Failed to submit near miss report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      {/* Full-screen on mobile, centered modal on sm+ */}
      <div className="relative bg-card w-full sm:max-w-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl">
        {/* Header — sticky */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border sticky top-0 bg-card z-10 rounded-t-2xl sm:rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <Icon name="AlertTriangle" size={18} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-foreground font-heading">{t('reportNearMiss')}</h2>
              <p className="text-xs text-muted-foreground hidden sm:block">{t('nearMissModalSubtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Scrollable form body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
            {/* Site Selector */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('site')} <span className="text-destructive">*</span>
              </label>
              <select
                value={form?.site_id}
                onChange={e => handleChange('site_id', e?.target?.value)}
                className={`w-full px-3 py-3 rounded-lg border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 min-h-[48px] ${errors?.site_id ? 'border-destructive' : 'border-border'}`}
                disabled={sitesLoading}
              >
                <option value="">{sitesLoading ? t('loading') : t('nearMissSelectSite')}</option>
                {sites?.map(site => (
                  <option key={site?.id} value={site?.id}>{site?.site_name}</option>
                ))}
              </select>
              {errors?.site_id && <p className="text-xs text-destructive mt-1">{errors?.site_id}</p>}
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('date')} <span className="text-destructive">*</span>
              </label>
              <input
                type="date"
                value={form?.date}
                onChange={e => handleChange('date', e?.target?.value)}
                className={`w-full px-3 py-3 rounded-lg border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 min-h-[48px] ${errors?.date ? 'border-destructive' : 'border-border'}`}
              />
              {errors?.date && <p className="text-xs text-destructive mt-1">{errors?.date}</p>}
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('nearMissColLocation')}</label>
              <input
                type="text"
                value={form?.location}
                onChange={e => handleChange('location', e?.target?.value)}
                placeholder="e.g. Level 3, East stairwell"
                className="w-full px-3 py-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 min-h-[48px]"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('description')} <span className="text-destructive">*</span>
              </label>
              <textarea
                value={form?.description}
                onChange={e => handleChange('description', e?.target?.value)}
                placeholder="Describe what happened, what could have gone wrong, and any contributing factors..."
                rows={4}
                className={`w-full px-3 py-3 rounded-lg border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 resize-none ${errors?.description ? 'border-destructive' : 'border-border'}`}
              />
              {errors?.description && <p className="text-xs text-destructive mt-1">{errors?.description}</p>}
            </div>

            {/* Risk Level */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('nearMissRiskLevel')} <span className="text-destructive">*</span>
              </label>
              <div className="flex gap-2 sm:gap-3">
                {RISK_LEVELS?.map(level => {
                  const colors = {
                    Low: 'border-emerald-300 bg-emerald-50 text-emerald-700',
                    Medium: 'border-amber-300 bg-amber-50 text-amber-700',
                    High: 'border-red-300 bg-red-50 text-red-700',
                  };
                  const selected = form?.risk_level === level;
                  return (
                    <label
                      key={level}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 cursor-pointer transition-all text-sm font-medium min-h-[48px] ${
                        selected ? colors?.[level] + ' ring-2 ring-offset-1 ring-current' : 'border-border bg-background text-muted-foreground hover:border-border/80'
                      }`}
                    >
                      <input
                        type="radio"
                        name="risk_level"
                        value={level}
                        checked={selected}
                        onChange={() => handleChange('risk_level', level)}
                        className="sr-only"
                      />
                      {level}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Photo Upload — moved above submit */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('nearMissPhotos')} <span className="text-xs text-muted-foreground">({t('nearMissPhotosHint')})</span>
              </label>
              <MobilePhotoInput
                onFilesSelected={(newFiles) => setPhotoFiles(prev => [...prev, ...newFiles]?.slice(0, 10))}
                multiple
                hint="Up to 10 photos · JPEG, PNG, WebP"
              />
              {photoFiles?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {photoFiles?.map((file, i) => (
                    <div key={i} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Upload preview ${i + 1}`}
                        className="w-16 h-16 object-cover rounded-lg border border-border"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-0 opacity-100 transition-opacity"
                      >
                        <Icon name="X" size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Follow-up Task */}
            <label className="flex items-start gap-3 p-4 rounded-xl border border-border bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="checkbox"
                checked={form?.follow_up_task}
                onChange={e => handleChange('follow_up_task', e?.target?.checked)}
                className="mt-0.5 w-4 h-4 rounded border-border text-accent focus:ring-accent"
              />
              <div>
                <p className="text-sm font-medium text-foreground">{t('nearMissFollowUpTask')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('nearMissFollowUpTaskDesc')}</p>
              </div>
            </label>

            {/* Spacer for sticky button on mobile */}
            <div className="h-2 sm:hidden" />
          </div>
        </form>

        {/* Sticky submit footer */}
        <div className="border-t border-border p-4 sm:p-6 bg-card sticky bottom-0 pb-safe">
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 min-h-[48px]" disabled={loading}>
              {t('cancel')}
            </Button>
            <Button type="button" variant="default" className="flex-1 min-h-[48px]" disabled={loading} onClick={handleSubmit}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  {t('nearMissSubmitting')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Icon name="Send" size={16} />
                  {t('nearMissSubmitReport')}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportNearMissModal;
